import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type {
  EisenInvoer,
  IndelingInvoer,
  KoppelingInvoer,
  Vacature,
  VacatureFilter,
  VacatureInvoer,
  Vacaturestatus,
  VacatureWijziging,
} from './typen.ts';

export const vacatureSleutels = {
  alle: ['vacatures'] as const,
  lijst: (filter: VacatureFilter) => ['vacatures', 'lijst', filter] as const,
  details: (id: string) => ['vacatures', id] as const,
};

/**
 * Zoekt vacatures op titel, met filters.
 * @param filter De zoekterm, status, bedrijf, indeling en paginering.
 * @returns De query met de gevonden vacatures.
 */
export function useVacatures(filter: VacatureFilter) {
  return useQuery({
    queryKey: vacatureSleutels.lijst(filter),
    queryFn: () => uitvoeren(api.GET('/api/v1/vacatures', { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Zoekt vacatures voor een keuzelijst, standaard alleen de open vacatures.
 * @param zoek De zoekterm.
 * @param status De status, of `undefined` voor alle statussen.
 * @returns De gevonden vacatures.
 */
export function zoekVacatures(zoek: string, status: Vacaturestatus | undefined = 'open'): Promise<Vacature[]> {
  return uitvoeren(
    api.GET('/api/v1/vacatures', {
      params: { query: { limiet: 25, ...(zoek ? { zoek } : {}), ...(status ? { status } : {}) } },
    }),
  );
}

/**
 * Haalt een vacature op met klant, eisen en indeling.
 * @param id Het id van de vacature.
 * @returns De query met de vacature.
 */
export function useVacature(id: string | null | undefined) {
  return useQuery({
    queryKey: vacatureSleutels.details(id ?? ''),
    queryFn: () => uitvoeren(api.GET('/api/v1/vacatures/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

/**
 * De mutaties op vacatures.
 * @returns De mutaties.
 */
export function useVacatureMutaties() {
  const alle = () => [vacatureSleutels.alle];
  const details = ({ id }: { id: string }) => [vacatureSleutels.details(id), vacatureSleutels.alle];
  return {
    maak: useApiMutatie((invoer: VacatureInvoer) => uitvoeren(api.POST('/api/v1/vacatures', { body: invoer })), alle),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: VacatureWijziging }) =>
        uitvoeren(api.PATCH('/api/v1/vacatures/{id}', { params: { path: { id } }, body: invoer })),
      details,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/vacatures/{id}', { params: { path: { id } } })),
      () => [vacatureSleutels.alle, ['matches'], ['contactmomenten']],
    ),
    zetStatus: useApiMutatie(
      ({ id, status }: { id: string; status: Vacaturestatus }) =>
        uitvoeren(api.PUT('/api/v1/vacatures/{id}/status', { params: { path: { id } }, body: { status } })),
      details,
    ),
    zetEisen: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: EisenInvoer }) =>
        uitvoeren(api.PUT('/api/v1/vacatures/{id}/eisen', { params: { path: { id } }, body: invoer })),
      details,
    ),
    zetIndeling: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: IndelingInvoer }) =>
        uitvoeren(api.PUT('/api/v1/vacatures/{id}/indeling', { params: { path: { id } }, body: invoer })),
      details,
    ),
    koppelBedrijf: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: KoppelingInvoer }) =>
        uitvoeren(api.PUT('/api/v1/vacatures/{id}/bedrijf', { params: { path: { id } }, body: invoer })),
      details,
    ),
    ontkoppelBedrijf: useApiMutatie(
      ({ id }: { id: string }) => uitvoeren(api.DELETE('/api/v1/vacatures/{id}/bedrijf', { params: { path: { id } } })),
      details,
    ),
  };
}
